import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { getFlattenTreeData } from '@futrli/predict/chart-of-accounts/utils';

import { COA_GROUP_FORM_FIELD_IDS } from './constants';
import { BALANCE_SHEET_CATEGORIES, COADisplayAccount, P_AND_L_CATEGORIES } from '../../../../chart-of-accounts/types';
import { useIsBalanceSheet } from '../../../hooks/useIsBalanceSheet';

export const useCoaGroupForm = (
  profitAndLossTree: COADisplayAccount | null,
  balanceSheetTree: COADisplayAccount | null,
  row?: COADisplayAccount
) => {
  const [groupName, setGroupName] = useState(row?.name || '');
  const [selectedCategory, setSelectedCategory] = useState(row?.category || '');
  const [selectedAccounts, setSelectedAccounts] = useState<COADisplayAccount[]>(
    row?.children?.filter(child => child.type === 'account') || []
  );
  const [errors, setErrors] = useState<Record<string, { id: string; message: string }>>({});

  const intl = useIntl();
  const hasBeenValidated = useRef(false);

  const isBalanceSheet = useIsBalanceSheet();

  const categoriesForDisplay = isBalanceSheet ? BALANCE_SHEET_CATEGORIES : P_AND_L_CATEGORIES;

  useEffect(() => {
    setGroupName(row?.name || '');
    setSelectedCategory(row?.category || '');
    setSelectedAccounts(row?.children?.filter(child => child.type === 'account') || []);

    setErrors({});
    hasBeenValidated.current = false;
  }, [row]);

  const accountsAutocompleteData = useMemo(() => {
    if (!selectedCategory) {
      return [];
    }

    return getFlattenTreeData(isBalanceSheet ? balanceSheetTree?.children : profitAndLossTree?.children).filter(
      d => d.type === 'account' && d.category === selectedCategory
    );
  }, [isBalanceSheet, balanceSheetTree, profitAndLossTree, selectedCategory]);

  const validate = useCallback(() => {
    const newErrors: Record<string, { id: string; message: string }> = {};

    // Group name
    if (!groupName.trim().length) {
      newErrors.name = {
        id: COA_GROUP_FORM_FIELD_IDS.GROUP_NAME,
        message: intl.formatMessage({
          id: 'reporting.validation.group_name_is_required',
          defaultMessage: 'Group name is required'
        })
      };
    }

    // Category
    if (!selectedCategory.trim().length) {
      newErrors.category = {
        id: COA_GROUP_FORM_FIELD_IDS.CATEGORY,
        message: intl.formatMessage({
          id: 'reporting.validation.group_category_is_required',
          defaultMessage: 'Group category is required'
        })
      };
    }

    setErrors(newErrors);
    hasBeenValidated.current = true;
    return Object.keys(newErrors).length === 0;
  }, [groupName, selectedCategory, intl]);

  const revalidate = useCallback(() => {
    if (hasBeenValidated.current) {
      validate();
    }
  }, [validate]);

  return {
    groupName,
    setGroupName,
    category: selectedCategory,
    categoryOptions: categoriesForDisplay,
    accountOptions: accountsAutocompleteData,
    selectedAccounts,
    setSelectedAccounts,
    setSelectedCategory,
    errors,
    validate,
    revalidate,
    isBalanceSheet,
    isEditMode: !!row
  };
};
